import React from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, Smartphone, Bike, Store, ArrowRight, Utensils, ShoppingBag, Clock, Heart, Download, Package } from 'lucide-react';
import Logo, { TocoaScript } from '../components/Logo';
import { LanguageSwitch } from '../components/LanguageToggle';
import { IMAGES } from '../lib/constants';

const portals = [
  { to: '/admin/login', title: 'Panel de Administrador', desc: 'Gestión global de pedidos, restaurantes, conductores y pagos.', icon: LayoutDashboard, tag: 'Web' },
  { to: '/app', title: 'App de Clientes', desc: 'Comida, Mandaditos 7, envíos y seguimiento en tiempo real.', icon: Smartphone, tag: 'Móvil' },
  { to: '/driver', title: 'Driver App', desc: 'Recibe pedidos, navega y entrega. Conecta, Recoge, Entrega, Gana.', icon: Bike, tag: 'Móvil' },
  { to: '/restaurant/login', title: 'Portal de Restaurantes', desc: 'Administra tu menú, precios y pedidos entrantes.', icon: Store, tag: 'Web' },
];

const features = [
  { icon: Utensils, title: 'Comida', desc: 'Tus restaurantes favoritos' },
  { icon: ShoppingBag, title: 'Mandaditos 7', desc: 'Farmacia, supermercado, envíos y más' },
  { icon: Clock, title: 'Rápido y seguro', desc: 'Entregas confiables' },
  { icon: Heart, title: 'Hecho para Tocoa', desc: 'Tu ciudad, nuestra prioridad' },
];

const DL = `${process.env.REACT_APP_BACKEND_URL}/api/descargas`;

const downloads = [
  { file: 'delivery7-cliente.apk', title: 'Delibery 7', desc: 'App de clientes · pide comida y mandaditos', icon: Smartphone, testid: 'dl-customer-apk' },
  { file: 'delivery7-repartidor.apk', title: 'Delibery 7 Repartidor', desc: 'App de repartidores · recibe y entrega pedidos', icon: Bike, testid: 'dl-driver-apk' },
];

const bundles = [
  { file: 'delivery7-cliente-release.aab', label: 'AAB firmado · Clientes', testid: 'dl-customer-aab' },
  { file: 'delivery7-repartidor-release.aab', label: 'AAB firmado · Repartidores', testid: 'dl-driver-aab' },
];

const Landing = () => (
  <div className="min-h-screen bg-[#F7F7F9] text-[#1E2130]">
    <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
      <Logo size="md" />
      <div className="hidden md:flex items-center gap-4 text-sm font-medium text-gray-600">
        <span>Comida</span><span className="text-gray-300">•</span><span>Mandaditos 7</span><span className="text-gray-300">•</span><span>Envíos</span><span className="text-gray-300">•</span><span>Todo en Tocoa</span>
      </div>
      <div className="flex items-center gap-4">
        <a href="#descargar" data-testid="header-download-link" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-[#EE3B36] hover:text-[#c92d29]"><Download size={16} /> Descargar la app</a>
        <LanguageSwitch />
        <TocoaScript className="hidden sm:block text-3xl" />
      </div>
    </header>

    <section className="max-w-6xl mx-auto px-6 pt-8 pb-12 grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <p className="text-[#EE3B36] font-semibold text-sm mb-3 tracking-wide uppercase">Plataforma completa de delivery</p>
        <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">Tu ciudad, <span className="text-[#EE3B36]">en tus manos</span></h1>
        <p className="mt-4 text-lg text-gray-600 max-w-lg">Comida de tus restaurantes favoritos, Mandaditos 7, envíos y mucho más. Selecciona una plataforma para explorar la demo.</p>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {portals.map((p) => (
            <Link key={p.to} to={p.to} data-testid={`portal-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="group bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center"><p.icon size={22} /></div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.tag}</span>
              </div>
              <h3 className="mt-4 font-bold text-base">{p.title}</h3>
              <p className="text-sm text-gray-500 mt-1 leading-snug">{p.desc}</p>
              <div className="mt-3 flex items-center gap-1 text-[#EE3B36] text-sm font-semibold">Abrir <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></div>
            </Link>
          ))}
        </div>
      </div>
      <div className="relative hidden lg:block">
        <div className="absolute -inset-6 bg-[#FDECEC] rounded-[40px] rotate-3" />
        <img src={IMAGES.rider} alt="Repartidor Delibery 7" className="relative rounded-[32px] shadow-2xl object-cover w-full h-[520px]" />
        <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-4 flex items-center gap-4 shadow-lg">
          <div className="w-12 h-12 rounded-xl bg-[#EE3B36] text-white flex items-center justify-center font-extrabold text-xl">7</div>
          <div>
            <p className="font-bold">Delibery 7</p>
            <p className="text-sm text-gray-500">Comida • Mandaditos 7 • Envíos • Todo en Tocoa</p>
          </div>
        </div>
      </div>
    </section>

    <section className="max-w-6xl mx-auto px-6 pb-16">
      <div className="bg-[#FDECEC] rounded-3xl p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        {features.map((f) => (
          <div key={f.title} className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white text-[#EE3B36] flex items-center justify-center shrink-0"><f.icon size={22} /></div>
            <div><p className="font-bold text-sm">{f.title}</p><p className="text-xs text-gray-600">{f.desc}</p></div>
          </div>
        ))}
      </div>
    </section>

    <section id="descargar" className="bg-[#120b0b] text-white">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex items-center gap-2 text-[#ef8f8c] text-xs font-bold tracking-[0.25em] uppercase">
          <Download size={14} /> Descargar
        </div>
        <h2 className="text-base md:text-lg font-extrabold mt-3">Instala Delibery 7 en tu teléfono Android</h2>
        <p className="text-sm text-white/55 mt-2 max-w-xl">Descarga el archivo, ábrelo y permite la instalación de orígenes desconocidos cuando Android lo pida. Las apps también estarán pronto en Google Play y en el App Store.</p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-3xl">
          {downloads.map((d) => (
            <a key={d.file} href={`${DL}/${d.file}`} download data-testid={d.testid}
              className="group bg-white/[0.06] hover:bg-white/[0.1] border border-white/10 rounded-2xl p-5 transition-colors duration-200">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-[#EE3B36] text-white flex items-center justify-center"><d.icon size={22} /></div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/70">APK · Android</span>
              </div>
              <h3 className="mt-4 font-bold text-base">{d.title}</h3>
              <p className="text-sm text-white/50 mt-1 leading-snug">{d.desc}</p>
              <div className="mt-3 flex items-center gap-1 text-[#ef8f8c] text-sm font-semibold">Descargar APK <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></div>
            </a>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 max-w-3xl">
          <p className="text-xs font-bold text-white/40 uppercase tracking-wider flex items-center gap-1.5"><Package size={13} /> Para publicar en Google Play (uso interno)</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
            {bundles.map((b) => (
              <a key={b.file} href={`${DL}/${b.file}`} download data-testid={b.testid} className="text-sm font-semibold text-white/70 hover:text-white underline underline-offset-4 decoration-white/30">{b.label}</a>
            ))}
            <a href={`${DL}/`} data-testid="dl-all-files" className="text-sm font-semibold text-[#ef8f8c] hover:text-white">Ver todos los archivos y capturas &rarr;</a>
          </div>
        </div>
      </div>
    </section>

    <footer className="bg-[#120b0b] border-t border-white/10 text-white/50">
      <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm">
        <p>&copy; 2026 Delibery 7 &middot; Tocoa, Colón, Honduras</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2 font-semibold">
          <Link to="/privacidad" data-testid="footer-privacy" className="hover:text-white">Privacidad</Link>
          <Link to="/terminos" data-testid="footer-terms" className="hover:text-white">Términos</Link>
          <Link to="/eliminar-cuenta" data-testid="footer-delete" className="hover:text-white">Eliminar mi cuenta</Link>
        </div>
      </div>
    </footer>
  </div>
);

export default Landing;
